/**
 * Service: Site Content (P6.3 content store)
 *
 * Versioned, hashed, per-locale editable copy. Keys are grouped by section
 * (one section per screen). Reads are public — the landing page renders from
 * this store. Writes are super_admin only (enforced by RLS) and every write
 * appends an immutable row to site_content_versions.
 */
import { supabase } from "@/integrations/supabase/client";

export const DEFAULT_LOCALE = "en";

export interface SiteContentRow {
  id: string;
  section: string;
  content_key: string;
  locale: string;
  value: string;
  content_hash: string;
  version: number;
  updated_by: string | null;
  updated_at: string;
}

export interface SiteContentVersionRow {
  id: string;
  section: string;
  content_key: string;
  locale: string;
  version: number;
  old_value: string | null;
  new_value: string;
  content_hash: string;
  action: string;
  changed_by: string | null;
  changed_at: string;
}

/** Fetch all stored overrides for a section+locale as a key → value map. */
export async function getSectionContent(
  section: string,
  locale: string = DEFAULT_LOCALE
): Promise<{ data: Record<string, string>; error: Error | null }> {
  const { data, error } = await supabase
    .from("site_content" as any)
    .select("content_key, value")
    .eq("section", section)
    .eq("locale", locale);

  if (error) return { data: {}, error: error as unknown as Error };

  const map: Record<string, string> = {};
  for (const row of (data ?? []) as any[]) {
    if (typeof row.value === "string") map[row.content_key] = row.value;
  }
  return { data: map, error: null };
}

/** Full rows for the editor (includes version + hash metadata). */
export async function listSectionRows(section: string, locale: string = DEFAULT_LOCALE) {
  return supabase
    .from("site_content" as any)
    .select("*")
    .eq("section", section)
    .eq("locale", locale)
    .order("content_key");
}

/** Create or update a single key. The DB trigger bumps version + writes history. */
export async function upsertContentKey(params: {
  section: string;
  key: string;
  value: string;
  locale?: string;
}) {
  const { section, key, value, locale = DEFAULT_LOCALE } = params;

  const { data: existing } = await supabase
    .from("site_content" as any)
    .select("id")
    .eq("content_key", key)
    .eq("locale", locale)
    .maybeSingle();

  if (existing && (existing as any).id) {
    return supabase
      .from("site_content" as any)
      .update({ value, section } as any)
      .eq("id", (existing as any).id);
  }

  return supabase
    .from("site_content" as any)
    .insert({ section, content_key: key, locale, value } as any);
}

/** Remove an override so the key falls back to its code-side default. */
export async function resetContentKey(key: string, locale: string = DEFAULT_LOCALE) {
  return supabase
    .from("site_content" as any)
    .delete()
    .eq("content_key", key)
    .eq("locale", locale);
}

/** Append-only edit history for one key. */
export async function getKeyHistory(key: string, locale: string = DEFAULT_LOCALE) {
  return supabase
    .from("site_content_versions" as any)
    .select("*")
    .eq("content_key", key)
    .eq("locale", locale)
    .order("version", { ascending: false })
    .limit(50);
}

/* ------------------------------------------------------------------ assets */

export const SITE_ASSET_BUCKET = "site-assets";
const STORAGE_REF_PREFIX = "storage://";
const SIGNED_URL_TTL_SECONDS = 60 * 60 * 24 * 7; // 7 days

/** True when a stored value points at an upload in the site-assets bucket. */
export function isStorageRef(value: string): boolean {
  return typeof value === "string" && value.startsWith(STORAGE_REF_PREFIX);
}

export function storageRefToPath(value: string): string {
  return value.slice(STORAGE_REF_PREFIX.length);
}

/**
 * Upload a media file for a content key. The bucket is private (workspace
 * policy blocks public buckets), so reads go through signed URLs. Visitors
 * can read ONLY files currently referenced by site_content (policy
 * "site-assets: anyone can read published media"); drafts and replaced
 * uploads stay super-admin only.
 * Returns the `storage://<path>` reference to store as the key's value.
 */
export async function uploadSiteAsset(
  key: string,
  file: File
): Promise<{ ref: string | null; error: Error | null }> {
  const safeKey = key.replace(/[^a-zA-Z0-9._-]/g, "_");
  const ext = file.name.includes(".") ? file.name.split(".").pop() : "bin";
  const path = `${safeKey}/${Date.now()}.${ext}`;

  const { error } = await supabase.storage
    .from(SITE_ASSET_BUCKET)
    .upload(path, file, { cacheControl: "31536000", upsert: false, contentType: file.type });

  if (error) return { ref: null, error: error as unknown as Error };
  return { ref: `${STORAGE_REF_PREFIX}${path}`, error: null };
}

/** Resolve a stored asset value to a URL usable in <img>/<video>. */
export async function resolveAssetUrl(value: string): Promise<string> {
  if (!value) return "";
  if (!isStorageRef(value)) return value;
  const { data } = await supabase.storage
    .from(SITE_ASSET_BUCKET)
    .createSignedUrl(storageRefToPath(value), SIGNED_URL_TTL_SECONDS);
  return data?.signedUrl ?? "";
}
